"use client";

/**
 * Mobil yerleşim store'u (kaynak-referans `features/ui/state/MobileLayout.ts` mobx
 * sınıfı paritesi — MobX'e geri dönüş, zustand kaldırıldı).
 *
 * Dar viewport'ta (veya mobil tarayıcıda) tek-sütun yerleşimi etkinleştirir;
 * gezinti (nav) ve sohbet panellerinin genişletilme durumunu izler. Mobil
 * modda nav ve sohbet aynı anda genişletilemez (kaynak-referans karşılıklı dışlama
 * kuralı korundu).
 *
 * Kalıcılık referans-parite `makePersistent` ile: yalnızca `navExpanded` ve
 * `chatExpanded` kalıcıdır (kaynak-referans allowlist paritesi); `enabled` her oturumda
 * viewport'tan türetilir. kaynak-referans `deferUntilModulesLoaded` yerine modül-kapsamı
 * boot bloğunda SSR-korumalı mobx `reaction(WindowStore.windowSize)` ile
 * pencere boyutu takibi yapılır (referans-parite).
 *
 * Histerezis: mobil moda giriş < 640px, çıkış >= 768px — eşik bandında
 * titremeyi (flicker) önler.
 */

import { makeAutoObservable, reaction } from "mobx";

import { Platform } from "@app/features/platform/types/Platform";
import { makePersistent } from "@app/features/platform/utils/MobXPersistence";
import { WindowStore } from "@app/features/window/state/WindowStore";

const MOBILE_ENABLE_BREAKPOINT = 640;
const MOBILE_DISABLE_BREAKPOINT = 768;

const shouldForceMobileLayout = (): boolean => Platform.isMobileBrowser;

const getInitialMobileEnabled = (): boolean => {
  if (shouldForceMobileLayout()) {
    return true;
  }
  if (typeof window === "undefined") {
    return false;
  }
  return window.innerWidth < MOBILE_ENABLE_BREAKPOINT;
};

class MobileLayout {
  /** Gezinti paneli genişletilmiş mi? (kalıcı) */
  navExpanded = true;
  /** Sohbet paneli genişletilmiş mi? (kalıcı) */
  chatExpanded = false;
  /** Mobil yerleşim etkin mi? (viewport'tan türetilir; kalıcı DEĞİL) */
  enabled = getInitialMobileEnabled();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    // kaynak-referans `makePersistent` allowlist paritesi: yalnızca panel durumları.
    void makePersistent(this, "MobileLayout", ["navExpanded", "chatExpanded"]);
  }

  /** Mobil yerleşim etkin mi? (kaynak-referans `isEnabled` paritesi) */
  isEnabled(): boolean {
    return this.enabled;
  }

  /** Pencere boyutu değişince mobil modu histerezisle yeniden değerlendirir. */
  handleWindowSizeChange(): void {
    const windowSize = WindowStore.windowSize;
    const forceMobile = shouldForceMobileLayout();
    const threshold = this.enabled ? MOBILE_DISABLE_BREAKPOINT : MOBILE_ENABLE_BREAKPOINT;
    const widthBased = windowSize.width < threshold;
    const newEnabled = forceMobile || widthBased;
    if (newEnabled === this.enabled) {
      return;
    }
    this.enabled = newEnabled;
    if (newEnabled) {
      this.navExpanded = this.navExpanded && !this.chatExpanded;
    }
  }

  /** Panel genişletme durumlarını günceller (mobilde karşılıklı dışlama). */
  updateState(data: { navExpanded?: boolean; chatExpanded?: boolean }): void {
    const hasChanges =
      (data.navExpanded !== undefined && data.navExpanded !== this.navExpanded) ||
      (data.chatExpanded !== undefined && data.chatExpanded !== this.chatExpanded);
    if (!hasChanges) {
      return;
    }
    if (data.navExpanded !== undefined) {
      this.navExpanded = data.navExpanded;
      if (data.navExpanded && this.enabled && this.chatExpanded) {
        this.chatExpanded = false;
      }
    }
    if (data.chatExpanded !== undefined) {
      this.chatExpanded = data.chatExpanded;
      if (data.chatExpanded && this.enabled && this.navExpanded) {
        this.navExpanded = false;
      }
    }
  }

  /** Mobil yerleşim etkin mi? (kaynak-referans `isMobileLayout` paritesi) */
  isMobileLayout(): boolean {
    return this.enabled;
  }

  /** Platform mobil tarayıcı olarak algılandı mı? (kaynak-referans getter paritesi) */
  get platformMobileDetected(): boolean {
    return shouldForceMobileLayout();
  }

  isNavExpanded(): boolean {
    return this.navExpanded;
  }

  isChatExpanded(): boolean {
    return this.chatExpanded;
  }
}

/** Mobil yerleşim store singleton'ı (kaynak-referans default-export paritesi). */
export const MobileLayoutStore = new MobileLayout();
export default MobileLayoutStore;

/** Platform mobil tarayıcı olarak algılandı mı? (geriye dönük yardımcı) */
export function isPlatformMobileDetected(): boolean {
  return shouldForceMobileLayout();
}

// Boot: pencere boyutu değişimini izle (bir kez, yalnızca tarayıcıda) —
// kaynak-referans `initWindowSync()` reaction paritesi.
if (typeof window !== "undefined") {
  MobileLayoutStore.handleWindowSizeChange();
  reaction(
    () => WindowStore.windowSize,
    () => MobileLayoutStore.handleWindowSizeChange(),
  );
}
